import { useState, useEffect } from "react";

import PropTypes from "prop-types";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";

import InputDate from "components/RH/inputs/InputDate";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskUtil from "utils/MaskUtil";
import useQuery from "utils/useQuery";
import { validarCPF } from "utils/ValidacaoUtils";

import ListaFuncionario from "pages/RH/listaFuncionario";

const Dependentes = ({ dados, tiposParentesco }) => {
  const { setLoading } = useLoading();
  const auth = useAuth();
  const notify = useNotification();
  const query = useQuery();
  const Requicicao = new Api();

  // Usa apenas os tipos de parentesco carregados da API
  const parentescos = tiposParentesco || [];

  const startData = {
    nome: "",
    dataNascimento: "",
    parentesco: "",
    cpf: ""
  };

  const [PossuiDependentes, setPossuiDependentes] = useState(false);
  const [Lista, setLista] = useState([]);
  const [dependente, setDependente] = useState(startData);

  const GetDependente = async () => {
    const data = await Requicicao.Get({
      endpoint: "/Funcionarios/Dependente",
      params: { funcionarioId: dados.id },
      config: auth.GetHeaders()
    });
    if (data.codigoEstadoHttp === 200) {
      // Normaliza os dados para o novo padrão do backend
      const dependentesNormalizados = data.objetoResposta.map(dep => ({
        ...dep,
        dataNascimento: dep.nascimento, // Backend retorna 'nascimento'
        parentesco: dep.parentescoId, // Backend retorna 'parentescoId'
        parentescoDescricao: dep.parentescoDescricao // Backend retorna 'parentescoDescricao'
      }));
      setLista(dependentesNormalizados);
    }
    return true;
  };

  const UpdeteDependente = async (lista) => {
    try {
      setLoading(true);
      
      // Validação dos campos obrigatórios
      if (!dependente.nome) {
        notify({
          type: "error",
          message: "O nome do dependente é obrigatório."
        });
        return;
      }

      if (!dependente.cpf || !validarCPF(dependente.cpf)) {
        notify({
          type: "error",
          message: "O CPF do dependente é inválido."
        });
        return;
      }

      if (!dependente.dataNascimento) {
        notify({
          type: "error",
          message: "A data de nascimento do dependente é obrigatória."
        });
        return;
      }

      if (!dependente.parentesco) {
        notify({
          type: "error",
          message: "O parentesco do dependente é obrigatório."
        });
        return;
      }
      
      const data = { id: dados.id };
      
      // Formatar a data de nascimento para ISO string e usar o nome correto do campo
      const dependenteFormatado = {
        nome: dependente.nome.trim(), // Remove espaços em branco
        dataNascimento: dependente.dataNascimento ? (() => {
          try {
            if (dependente.dataNascimento instanceof Date) {
              return dependente.dataNascimento.toISOString();
            }
            if (typeof dependente.dataNascimento === 'string') {
              const [day, month, year] = dependente.dataNascimento.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            }
            return dependente.dataNascimento;
          } catch (error) {
            console.error("Erro ao formatar data de nascimento do dependente:", error);
            return dependente.dataNascimento;
          }
        })() : null,
        parentesco: parseInt(dependente.parentesco, 10), // Converte para número
        cpf: dependente.cpf // Mantém a formatação do CPF
      };

      // Normaliza todos os dependentes para o formato correto do PUT
      const dependentesParaEnviar = lista.map(dep => ({
        nome: dep.nome,
        dataNascimento: dep.dataNascimento || dep.nascimento, // Garante que use dataNascimento
        parentesco: typeof dep.parentesco === 'number' ? dep.parentesco : parseInt(dep.parentesco, 10), // Garante que seja número
        cpf: dep.cpf // Mantém a formatação do CPF
      }));

      // Adiciona o novo dependente formatado
      dependentesParaEnviar.push(dependenteFormatado);
      
      data.dependentes = dependentesParaEnviar;

      const response = await Requicicao.Put({
        endpoint: "/Funcionarios/Dependente",
        data,
        config: auth.GetHeaders()
      });

      if (response.codigoEstadoHttp === 200) {
        setDependente(startData);
        await GetDependente();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const RemoverDependente = (cpf) => {
    const lista = Lista.filter((x) => x.cpf !== cpf);
    UpdeteDependente(lista);
  };

  useEffect(() => {
    if (dados?.dependentes?.length > 0) {
      setPossuiDependentes(true);
      // Normaliza os dados para o novo padrão do backend
      const dependentesNormalizados = dados.dependentes.map(dep => ({
        ...dep,
        dataNascimento: dep.nascimento, // Backend retorna 'nascimento'
        parentesco: dep.parentescoId, // Backend retorna 'parentescoId'
        parentescoDescricao: dep.parentescoDescricao // Backend retorna 'parentescoDescricao'
      }));
      setLista(dependentesNormalizados);
    }
  }, [dados.dependentes]);

  return (
    <div className="row g-3">
      <div className="col-md-6 col-lg-3">
        <div className="d-flex flex-column">
          <label htmlFor="possuiDependentes" className="form-label">
            Possui Dependentes?
          </label>
          <InputSwitch
            id="possuiDependentes"
            checked={PossuiDependentes}
            onChange={(e) => setPossuiDependentes(e.value)}
            disabled={Lista.length > 0}
          />
        </div>
      </div>
      {PossuiDependentes ? (
        <>
          <div className="col-12">
            <h4>Cadastro de Dependentes</h4>
          </div>
          <div className="col-md-6 col-lg-3">
            <label>Nome do Dependente</label>
            <InputText
              value={dependente.nome}
              onChange={(e) =>
                setDependente({
                  ...dependente,
                  nome: e.target.value
                })
              }
              className="w-100"
            />
          </div>
          <div className="col-md-6 col-lg-3">
            <label>Data de Nascimento</label>
            <InputDate
              value={dependente.dataNascimento}
              onChange={(dateString) =>
                setDependente({
                  ...dependente,
                  dataNascimento: dateString
                })
              }
              placeholder="dd/mm/aaaa"
            />
          </div>
          <div className="col-md-6 col-lg-3">
            <label>Parentesco</label>
            <Dropdown
              value={dependente.parentesco}
              options={parentescos}
              onChange={(e) =>
                setDependente({
                  ...dependente,
                  parentesco: e.value
                })
              }
              className="w-100"
            />
          </div>
          <div className="col-md-6 col-lg-3">
            <label>CPF</label>
            <InputText
              value={dependente.cpf}
              onChange={(e) =>
                setDependente({
                  ...dependente,
                  cpf: MaskUtil.applyCpfMask(e.target.value)
                })
              }
              className="w-100"
            />
          </div>
          <div className="col-12">
            <div className="d-flex flex-row-reverse">
              <Button
                type="button"
                label="Adicionar Dependente"
                icon="pi pi-plus"
                onClick={() => UpdeteDependente(Lista)}
              />
            </div>
          </div>
          <div className="col-12">
            <DataTable value={Lista}>
              <Column field="nome" header="Nome" />
              <Column 
                field="parentesco" 
                header="Parentesco"
                body={(row) => {
                  // Usa a descrição que vem do backend, com fallback para busca local
                  if (row.parentescoDescricao) {
                    return row.parentescoDescricao;
                  }
                  const tipoParentesco = parentescos.find(tipo => tipo.value === row.parentesco);
                  return tipoParentesco ? tipoParentesco.label : row.parentesco;
                }}
              />
              <Column field="cpf" header="CPF" />
              <Column
                field="dataNascimento"
                header="Data de Nascimento"
                body={(row) => {
                  // Usa o campo dataNascimento (já normalizado)
                  if (row.dataNascimento) {
                    return MaskUtil.applyDataMask(row.dataNascimento);
                  }
                  return "";
                }}
              />
              <Column
                header="Ação"
                body={(row) => (
                  <Button
                    type="button"
                    label="Remover"
                    onClick={() => RemoverDependente(row.cpf)}
                  />
                )}
              />
            </DataTable>
          </div>
        </>
      ) : (
        <p>Não tem dependentes</p>
      )}
    </div>
  );
};

Dependentes.propTypes = {
  dados: PropTypes.shape({
    possuiDependentes: PropTypes.bool.isRequired,
    dependentes: PropTypes.arrayOf(
      PropTypes.shape({
        nome: PropTypes.string.isRequired,
        parentesco: PropTypes.string.isRequired,
        cpf: PropTypes.string.isRequired,
        dataNascimento: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.instanceOf(Date)
        ]).isRequired
      })
    ).isRequired
  }).isRequired,
  tiposParentesco: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ),
  dependente: PropTypes.shape({
    nome: PropTypes.string.isRequired,
    parentesco: PropTypes.string.isRequired,
    cpf: PropTypes.string.isRequired,
    dataNascimento: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]).isRequired
  }).isRequired
};

Dependentes.defaultProps = {
  tiposParentesco: []
};

export default Dependentes;
